import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["children", "adaptsToHeightChange", "title", "isOpen", "isExpandedOpen", "hasBorderTop", "hasBorderBottom", "wrapperPadding", "titleBodySpacing", "toggleColor", "customToggle", "disabled", "as", "wrapperElement", "onClick", "data-test", "index", "noAnimation", "showMoreLabel", "secondaryCTA", "footer", "ariaLevel"];
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { ChevronIcon } from '@sainsburys-tech/bolt-icons';
import { useResizeEvent } from '@sainsburys-tech/boltui-hooks';
import * as Styled from './Accordion.styles';

var getChildOfType = function getChildOfType(child, type) {
  return child.type && child.type === type;
};

var Accordion = function Accordion(_ref) {
  var _ref$singleOpenOnly = _ref.singleOpenOnly,
      singleOpenOnly = _ref$singleOpenOnly === void 0 ? false : _ref$singleOpenOnly,
      dataTest = _ref['data-test'],
      children = _ref.children;
  var defaultActiveItem = null;
  React.Children.map(children, function (child, index) {
    if (child.props.isOpen) {
      defaultActiveItem = index;
    }
  });

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      _isOpen = _useState2[0],
      toggleOpen = _useState2[1];

  var _useState3 = useState(defaultActiveItem),
      _useState4 = _slicedToArray(_useState3, 2),
      _activeItem = _useState4[0],
      toggleActive = _useState4[1];

  var onAccordionItemClick = function onAccordionItemClick(index) {
    if (singleOpenOnly) {
      toggleOpen(false);
      toggleActive(index);
    }
  };

  var onToggleClick = function onToggleClick() {
    toggleActive(null);
    toggleOpen(!_isOpen);
  };

  var buttonText = _isOpen ? 'Collapse all' : 'Expand all';
  return /*#__PURE__*/React.createElement(React.Fragment, null, !singleOpenOnly && children.length > 1 && /*#__PURE__*/React.createElement(Styled.ButtonAll, {
    "data-test": "".concat(dataTest, "-button-toggle"),
    onClick: onToggleClick
  }, buttonText), React.Children.map(children, function (child, index) {
    var _child$props = child.props,
        childDataTest = _child$props['data-test'],
        onClick = _child$props.onClick;
    var shouldItBeOpen = singleOpenOnly ? _activeItem === index : _isOpen;

    var handleClick = function handleClick(ItemIndex, ItemIsOpen) {
      onAccordionItemClick(index);
      onClick(ItemIndex, ItemIsOpen);
    };

    return /*#__PURE__*/React.createElement(AccordionItem, _extends({
      ref: child.ref
    }, child.props, {
      "data-test": "".concat(childDataTest).concat(index !== null ? "-".concat(index) : ''),
      isOpen: shouldItBeOpen,
      onClick: handleClick,
      index: index
    }));
  }));
};

Accordion.defaultProps = {
  singleOpenOnly: false,
  'data-test': 'component-accordion-wrapper'
};
Accordion.propTypes = {
  /**
   * Optional prop to specifiy whether the Accordion should only open one at a time
   */
  singleOpenOnly: PropTypes.bool,

  /**
   * Optional prop for test targetting
   */
  'data-test': PropTypes.string
};

var ExpandedContent = function ExpandedContent(_ref2) {
  var children = _ref2.children;
  return children;
};

ExpandedContent.propTypes = {
  /**
   * Specify the content of your Button
   */
  children: PropTypes.node
};
var AccordionItem = /*#__PURE__*/forwardRef(function (_ref3, forwardedRef) {
  var children = _ref3.children,
      adaptsToHeightChange = _ref3.adaptsToHeightChange,
      title = _ref3.title,
      isOpen = _ref3.isOpen,
      isExpandedOpen = _ref3.isExpandedOpen,
      hasBorderTop = _ref3.hasBorderTop,
      hasBorderBottom = _ref3.hasBorderBottom,
      wrapperPadding = _ref3.wrapperPadding,
      titleBodySpacing = _ref3.titleBodySpacing,
      toggleColor = _ref3.toggleColor,
      customToggle = _ref3.customToggle,
      disabled = _ref3.disabled,
      Element = _ref3.as,
      wrapperElement = _ref3.wrapperElement,
      onClick = _ref3.onClick,
      dataTest = _ref3['data-test'],
      index = _ref3.index,
      noAnimation = _ref3.noAnimation,
      showMoreLabel = _ref3.showMoreLabel,
      secondaryCTA = _ref3.secondaryCTA,
      footer = _ref3.footer,
      ariaLevel = _ref3.ariaLevel,
      props = _objectWithoutProperties(_ref3, _excluded);

  var _useState5 = useState(isOpen),
      _useState6 = _slicedToArray(_useState5, 2),
      _isOpen = _useState6[0],
      toggleOpen = _useState6[1];

  var _useState7 = useState(isExpandedOpen),
      _useState8 = _slicedToArray(_useState7, 2),
      _isExpandedOpen = _useState8[0],
      toggleExpanded = _useState8[1];

  var _useState9 = useState('0px'),
      _useState10 = _slicedToArray(_useState9, 2),
      _setHeight = _useState10[0],
      setHeightState = _useState10[1];

  useEffect(function () {
    toggleOpen(isOpen);
    setHeightState("0px");
  }, [isOpen]);
  var content = [];
  var expandedContent = [];
  var container = useRef(null);
  useResizeEvent({
    isEnabled: adaptsToHeightChange && _isOpen,
    resizeHandler: function resizeHandler() {
      setTimeout(function () {
        return setHeightState(container.current && container.current.scrollHeight ? "".concat(container.current.scrollHeight, "px") : '0px');
      }, 0);
    }
  });
  useEffect(function () {
    setTimeout(function () {
      return setHeightState(isOpen && container.current && container.current.scrollHeight ? "".concat(container.current.scrollHeight, "px") : '0px');
    }, 0);
  }, [isOpen]);
  useImperativeHandle(forwardedRef, function () {
    return {
      updateHeight: function updateHeight() {
        if (_isOpen) {
          setTimeout(function () {
            return setHeightState(container.current && container.current.scrollHeight ? "".concat(container.current.scrollHeight, "px") : '0px');
          }, 0);
        }
      }
    };
  });
  React.Children.forEach(children, function (child) {
    if (child && getChildOfType(child, ExpandedContent)) {
      expandedContent.push(child);
    } else if (child) {
      content.push(child);
    }
  });

  var toggleOpenfunc = function toggleOpenfunc() {
    setHeightState(!_isOpen && container.current && container.current.scrollHeight ? "".concat(container.current.scrollHeight, "px") : '0px');
    toggleOpen(!_isOpen);
    onClick(index, !_isOpen);
  };

  var toggleExpandedfunc = function toggleExpandedfunc() {
    toggleExpanded(!_isExpandedOpen);
    setHeightState("none");
  };

  var hasExpandedContent = expandedContent.length > 0;
  var showExpandedContent = hasExpandedContent && _isExpandedOpen; // Forces the open-by-default accordion to set an initial max height instead of being undefined/0px
  // If no animation, force to have the initial max height

  var setMaxHeight = noAnimation && _isOpen ? 'initial' : _setHeight;

  var suffixer = function suffixer(suffix) {
    return suffix !== null ? "-".concat(suffix) : '';
  };

  var prefixer = function prefixer(prefix) {
    return prefix !== null ? "".concat(prefix, "-") : '';
  }; // Builds a selector with additional descriptors added to the start and/or end, primarily for the aria attributes


  var selector = function selector(selectorString, prefix, suffix) {
    return "".concat(prefixer(prefix)).concat(selectorString).concat(suffixer(suffix));
  };

  return /*#__PURE__*/React.createElement(Styled.Wrapper, _extends({
    hasBorderTop: hasBorderTop,
    hasBorderBottom: hasBorderBottom,
    wrapperPadding: wrapperPadding,
    isOpen: _isOpen,
    "data-test": "".concat(dataTest).concat(suffixer(index)),
    as: wrapperElement
  }, props), /*#__PURE__*/React.createElement(Styled.Button, {
    "data-test": "".concat(dataTest, "-button-toggle"),
    disabled: disabled,
    onClick: toggleOpenfunc,
    "aria-controls": selector('accordion-content', dataTest, index),
    "aria-expanded": _isOpen,
    "aria-disabled": disabled,
    id: selector('accordion-control', dataTest, index)
  }, /*#__PURE__*/React.createElement(Styled.Title, {
    as: Element,
    "data-test": "".concat(dataTest, "-title"),
    role: "heading",
    "aria-level": ariaLevel,
    isOpen: _isOpen
  }, title), customToggle ? /*#__PURE__*/React.cloneElement(customToggle, {
    isOpen: _isOpen
  }) : /*#__PURE__*/React.createElement(Styled.Toggle, {
    isOpen: _isOpen
  }, /*#__PURE__*/React.createElement(ChevronIcon, {
    fill: toggleColor,
    width: "24px",
    height: "24px",
    dir: "down"
  }))), secondaryCTA, /*#__PURE__*/React.createElement(Styled.Container, {
    ref: container,
    maxHeight: setMaxHeight,
    role: "region",
    id: selector('accordion-content', dataTest, index),
    "aria-labelledby": selector('accordion-control', dataTest, index),
    "aria-hidden": !_isOpen,
    noAnimation: noAnimation
  }, /*#__PURE__*/React.createElement(Styled.InnerContainer, {
    "data-test": "".concat(dataTest, "-content"),
    titleBodySpacing: titleBodySpacing
  }, content, showExpandedContent && /*#__PURE__*/React.createElement("div", {
    "data-test": "".concat(dataTest, "-content-expanded")
  }, expandedContent))), _isOpen && !_isExpandedOpen && hasExpandedContent && /*#__PURE__*/React.createElement(Styled.ButtonLink, {
    "data-test": "".concat(dataTest, "-button-show-more"),
    onClick: toggleExpandedfunc
  }, showMoreLabel), _isOpen && _isExpandedOpen && hasExpandedContent && /*#__PURE__*/React.createElement(Styled.ButtonLink, {
    "data-test": "".concat(dataTest, "-button-show-less"),
    onClick: toggleExpandedfunc
  }, "Show less"), footer ? /*#__PURE__*/React.cloneElement(footer, {
    isOpen: _isOpen,
    toggleOpenfunc: toggleOpenfunc
  }) : null);
});
AccordionItem.propTypes = {
  /**
   * Specify the content of your Accordion
   */
  children: PropTypes.node,

  /**
   * Optional prop to change the Show More label
   */
  showMoreLabel: PropTypes.string,

  /**
   * Optional prop to adapt height on window resize
   */
  adaptsToHeightChange: PropTypes.bool,

  /**
   * Specify an optional data-test attribute to be added to your Accordion for e2e testing
   */
  'data-test': PropTypes.string,

  /**
   * Optional Prop to specifiy whether the Accordion should be open by default, or not
   */
  isOpen: PropTypes.bool,

  /**
   * Optional Prop to specifiy whether the Accordion.Expanded component should be open by default, or not
   */
  isExpandedOpen: PropTypes.bool,

  /**
   * Optional prop to specifiy whether the Accordion should be disabled, or not
   */
  disabled: PropTypes.bool,

  /**
   * Optional prop to specify the role of the Accordion title
   */
  as: PropTypes.node,

  /**
   * Optional prop to specify the role of the Accordion wrapper
   */
  wrapperElement: PropTypes.node,

  /**
   * Optional prop to pass through onClick events when Accordion is clicked
   */
  onClick: PropTypes.func,

  /**
   * Optional prop to specify the padding around the Accordion
   */
  wrapperPadding: PropTypes.number,

  /**
   * Optional prop to specify the spacing between the Accordion title and body
   */
  titleBodySpacing: PropTypes.element,

  /**
   * Optional prop to specify the toggle colour
   */
  toggleColor: PropTypes.string,

  /**
   * Optional prop to provide a custom React element for the toggle
   */
  customToggle: PropTypes.node,

  /**
   * Optional prop to provide a secondaryCTA
   */
  secondaryCTA: PropTypes.node,

  /**
   * Optional prop to provide a footer
   */
  footer: PropTypes.node,

  /**
   * Optional prop to remove the animation
   */
  noAnimation: PropTypes.bool,

  /**
   * Optional prop to contain index of accordion item
   */
  index: PropTypes.number,

  /**
   * Optional prop to set aria-level attr
   */
  ariaLevel: PropTypes.number
};
AccordionItem.defaultProps = {
  showMoreLabel: 'Show more',
  adaptsToHeightChange: false,
  disabled: false,
  as: 'h4',
  isOpen: false,
  isExpandedOpen: false,
  onClick:
  /* istanbul ignore next */
  function onClick() {},
  'data-test': 'component-accordion',
  index: null,
  noAnimation: false,
  ariaLevel: 2
};
Accordion.Item = AccordionItem;
Accordion.Expanded = ExpandedContent;
export default Accordion;